import { FunctionComponent } from "preact";
import { useEffect, useMemo, useState } from "preact/hooks";
import { useStore } from "@nanostores/preact";
import { useLocation } from "wouter-preact";
import { useTranslator } from "../../hooks/i18n.ts";
import { $isLogined, $isSubAccount, $organizationName, $username, expireUserInfo, updateUserInfo } from "../../store/user_info.ts";
import { $arabicLayoutDir, $lang, setArabicLayoutDir, setLang, type ArabicLayoutDir, type LanguageCode } from "../../store/browser_var.ts";
import { gotoLoginPage } from "../../router_utils.ts";
import { requestDelete, requestGet, requestWithParams } from "../../utils/request.ts";
import { useToast } from "../../components/toast/toast.tsx";
import { PageHeader } from "../../components/ui/page_header.tsx";
import { Section } from "../../components/ui/section.tsx";
import { Icon } from "../../components/icon/icon.tsx";

const Modal = ({ title, visible, onClose, children }: { title: string, visible: boolean, onClose: () => void, children: any }) => {
    if (!visible) return null;
    return (
        <div style={{
            position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
            background: "rgba(0,0,0,0.7)", zIndex: 1000,
            display: "flex", alignItems: "center", justifyContent: "center"
        }}>
            <div style={{
                background: "var(--pico-background-color)",
                padding: "2rem", borderRadius: "8px",
                width: "90%", maxWidth: "500px",
                boxShadow: "0 10px 30px rgba(0,0,0,0.5)"
            }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
                    <h4 style={{ margin: 0 }}>{title}</h4>
                    <button class="outline secondary" style={{ border: "none", padding: "0.2rem" }} onClick={onClose}>
                        <Icon name="x" size={20} />
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
};

type UserInfoResponse = {
    username: string;
    organization_name: string;
    adv_expire: number;
};

const TZ_LIST = [
    "Asia/Shanghai",
    "Etc/UTC",
    "Etc/GMT+1",
    "Etc/GMT+2",
    "Etc/GMT+3",
    "Etc/GMT+4",
    "Etc/GMT+5",
    "Etc/GMT+6",
    "Etc/GMT+7",
    "Etc/GMT+8",
    "Etc/GMT+9",
    "Etc/GMT+10",
    "Etc/GMT+11",
    "Etc/GMT+12",
    "Etc/GMT-1",
    "Etc/GMT-2",
    "Etc/GMT-3",
    "Etc/GMT-4",
    "Etc/GMT-5",
    "Etc/GMT-6",
    "Etc/GMT-7",
    "Etc/GMT-8",
    "Etc/GMT-9",
    "Etc/GMT-10",
    "Etc/GMT-11",
    "Etc/GMT-12",
    "Etc/GMT-13",
    "Etc/GMT-14",
];

const setCookie = (name: string, value: string, maxAgeDays: number) => {
    const maxAge = Math.floor(maxAgeDays * 24 * 60 * 60);
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/`;
};

const getCookie = (name: string) => {
    const cookies = document.cookie.split(";");
    for (const c of cookies) {
        const [k, ...rest] = c.trim().split("=");
        if (k === name) return decodeURIComponent(rest.join("="));
    }
    return "";
};

export const UserSettingsPage: FunctionComponent = (_) => {
    const t = useTranslator();
    const toast = useToast();
    const isLogined = useStore($isLogined);
    const isSubAccount = useStore($isSubAccount);
    const username = useStore($username);
    const organizationName = useStore($organizationName);
    const [location, _setLocation] = useLocation();
    const [isBusy, setIsBusy] = useState(false);

    // Redirect sub-accounts
    useEffect(() => {
        if (isSubAccount) {
            toast(t({ zh: "子账户无权访问此页面", en: "Access Denied" }), "error");
            _setLocation("/overview");
        }
    }, [isSubAccount, _setLocation, toast, t]);

    const [orgNameInput, setOrgNameInput] = useState("");
    const [oldPwd, setOldPwd] = useState("");
    const [newPwd, setNewPwd] = useState("");
    const [newPwdRepeat, setNewPwdRepeat] = useState("");
    const [theme, setTheme] = useState(getCookie("theme") || "");
    const [tz, setTz] = useState(getCookie("tz") || "");
    const currentLang = useStore($lang);
    const [langInput, setLangInput] = useState<LanguageCode>(currentLang);
    const arabicLayoutDir = useStore($arabicLayoutDir);
    const [arabicLayoutDirInput, setArabicLayoutDirInput] = useState<ArabicLayoutDir>(arabicLayoutDir);

    // Email Settings
    // Deprecated: SMTP settings are now managed globally by admin
    // const [smtpServer, setSmtpServer] = useState("smtp.gmail.com");
    // const [smtpPort, setSmtpPort] = useState(587);
    // const [smtpUser, setSmtpUser] = useState("");
    // const [smtpPass, setSmtpPass] = useState("");
    // const [senderEmail, setSenderEmail] = useState("");
    
    const [recipientEmail, setRecipientEmail] = useState("");
    const [enableAlerts, setEnableAlerts] = useState(false);

    // Sub-Account State
    const [subAccounts, setSubAccounts] = useState<any[]>([]);
    const [showSubModal, setShowSubModal] = useState(false);
    const [subUsername, setSubUsername] = useState("");
    const [subPassword, setSubPassword] = useState("");
    const [subRemark, setSubRemark] = useState("");
    const subAccountLimitReached = subAccounts.length >= 5;
    const subUsernameRegex = useMemo(() => /^[A-Za-z0-9][A-Za-z0-9._-]{6,62}[A-Za-z0-9]$/, []);

    // Sub-Account Permission Modal
    const [showPermModal, setShowPermModal] = useState(false);
    const [currentSubId, setCurrentSubId] = useState<number | null>(null);
    const [allCameras, setAllCameras] = useState<any[]>([]);
    const [selectedCameras, setSelectedCameras] = useState<number[]>([]);

    // System Contact Info
    const [contactInfo, setContactInfo] = useState<{
        contact_name: string;
        whatsapp: string;
        email: string;
        website: string;
        wechat_id: string;
    } | null>(null);

    useEffect(() => {
        setLangInput(currentLang);
    }, [currentLang]);

    useEffect(() => {
        setArabicLayoutDirInput(arabicLayoutDir);
    }, [arabicLayoutDir]);

    useEffect(() => {
        document.title = t({ zh: "用户设置", en: "Settings" });
    }, [t]);

    useEffect(() => {
        if (!isLogined) {
            gotoLoginPage(location ?? "/settings");
        }
    }, [isLogined, location]);

    useEffect(() => {
        setOrgNameInput(organizationName);
    }, [organizationName]);

    const refreshSubAccounts = useMemo(() => async () => {
        try {
            const resp = await requestGet<{ code: number; data: any[] }>("/v2/sub_account/list");
            if (resp.code === 200) {
                setSubAccounts(resp.data);
            }
        } catch (e) {
            console.error(e);
        }
    }, []);

    const refreshAllCameras = useMemo(() => async () => {
        try {
            const resp = await requestGet<{ cameras: any[] }>("/v2/camera", { fields: "camera_name" });
            if (resp.cameras) {
                setAllCameras(resp.cameras);
            }
        } catch (e) {
            console.error(e);
        }
    }, []);

    useEffect(() => {
        if (isLogined) {
            requestGet<any>("/api/user/email_settings").then(resp => {
                if (resp.code === 200 && resp.data) {
                    const d = resp.data;
                    // SMTP fields are deprecated
                    // setSmtpServer(d.smtp_server);
                    // setSmtpPort(d.smtp_port);
                    // setSmtpUser(d.smtp_username);
                    // setSenderEmail(d.sender_email);
                    setRecipientEmail(d.recipient_email);
                    setEnableAlerts(d.enable_alerts);
                }
            }).catch(console.error);
            requestGet<any>("/v2/system/contact").then(resp => {
                if (resp.code === 200 && resp.data) {
                    setContactInfo(resp.data);
                }
            }).catch(console.error);
            refreshSubAccounts();
        }
    }, [isLogined]);

    const createSubAccountAction = useMemo(() => async () => {
        if (subAccountLimitReached) {
            toast(t({ zh: "子账户最多 5 个", en: "Up to 5 sub-accounts" }), "warn");
            return;
        }
        const username = subUsername.trim();
        if (username.length < 8 || username.length > 64) {
            toast(t({ zh: "用户名长度需为 8-64", en: "Username length must be 8-64" }), "warn");
            return;
        }
        if (!subUsernameRegex.test(username)) {
            toast(t({ zh: "用户名仅允许字母数字及 ._- 且首尾为字母数字", en: "Username allows A-Z a-z 0-9 . _ - (start/end alnum)" }), "warn");
            return;
        }

        const password = subPassword;
        if (password !== password.trim()) {
            toast(t({ zh: "密码首尾不能有空格", en: "Password cannot start/end with spaces" }), "warn");
            return;
        }
        if (password.length < 8 || password.length > 64) {
            toast(t({ zh: "密码长度需为 8-64", en: "Password length must be 8-64" }), "warn");
            return;
        }
        if (password.trim().length <= 0) {
            toast(t({ zh: "密码不能为空", en: "Password cannot be blank" }), "warn");
            return;
        }
        if (password === username) {
            toast(t({ zh: "密码不能与用户名相同", en: "Password cannot equal username" }), "warn");
            return;
        }
        if (!/[A-Za-z]/.test(password) || !/\d/.test(password)) {
            toast(t({ zh: "密码需包含字母和数字", en: "Password must include letters and numbers" }), "warn");
            return;
        }

        const remark = subRemark.trim();
        if (remark.length > 50) {
            toast(t({ zh: "备注最多 50 个字符", en: "Remark max 50 characters" }), "warn");
            return;
        }
        try {
            setIsBusy(true);
            await requestWithParams("POST", "/v2/sub_account/create", {
                username,
                password,
                remark
            });
            toast(t({ zh: "子账户创建成功", en: "Sub-account created" }), "success");
            setShowSubModal(false);
            setSubUsername("");
            setSubPassword("");
            setSubRemark("");
            refreshSubAccounts();
        } catch (e) {
            toast((e as Error)?.message ?? "Failed", "error");
        } finally {
            setIsBusy(false);
        }
    }, [subAccountLimitReached, subUsername, subPassword, subRemark, refreshSubAccounts, toast, t, subUsernameRegex]);

    const deleteSubAccountAction = useMemo(() => async (id: number) => {
        if (!confirm(t({ zh: "确定删除该子账户吗？", en: "Delete this sub-account?" }))) return;
        try {
            setIsBusy(true);
            await requestWithParams("DELETE", "/v2/sub_account/delete", { id });
            toast(t({ zh: "已删除", en: "Deleted" }), "success");
            refreshSubAccounts();
        } catch (e) {
            toast((e as Error)?.message ?? "Failed", "error");
        } finally {
            setIsBusy(false);
        }
    }, [refreshSubAccounts, toast, t]);

    const openPermModal = (sub: any) => {
        setCurrentSubId(sub.id);
        setSelectedCameras(sub.assigned_cameras || []);
        refreshAllCameras();
        setShowPermModal(true);
    };

    const savePermAction = useMemo(() => async () => {
        if (!currentSubId) return;
        try {
            setIsBusy(true);
            await requestWithParams("POST", "/v2/sub_account/assign_camera", {
                sub_id: currentSubId,
                camera_ids: selectedCameras
            });
            toast(t({ zh: "权限已更新", en: "Permissions updated" }), "success");
            setShowPermModal(false);
            refreshSubAccounts();
        } catch (e) {
            toast((e as Error)?.message ?? "Failed", "error");
        } finally {
            setIsBusy(false);
        }
    }, [currentSubId, selectedCameras, refreshSubAccounts, toast, t]);

    const refreshUserInfo = useMemo(() => async () => {
        try {
            const resp = await requestGet<UserInfoResponse>("/v2/user");
            updateUserInfo(resp);
        } catch {
            expireUserInfo();
        }
    }, []);

    const saveOrgAction = useMemo(() => async () => {
        if (orgNameInput.trim().length <= 0 || orgNameInput.trim().length > 50) {
            toast(t({ zh: "请输入有效的组织名称", en: "Please input a valid organization name" }), "warn");
            return;
        }
        try {
            setIsBusy(true);
            await requestWithParams("PATCH", "/v2/user", { organization_name: orgNameInput.trim() });
            await refreshUserInfo();
            toast(t({ zh: "保存成功", en: "Saved" }), "success");
        } catch (e) {
            toast((e as Error)?.message ?? "Request Failed", "error");
        } finally {
            setIsBusy(false);
        }
    }, [orgNameInput, refreshUserInfo, toast, t]);

    const changePasswordAction = useMemo(() => async () => {
        if (oldPwd.length <= 2 || newPwd.length <= 2 || oldPwd.length > 64 || newPwd.length > 64) {
            toast(t({ zh: "请输入有效用户名密码", en: "Please input valid passwords" }), "warn");
            return;
        }
        if (newPwd !== newPwdRepeat) {
            toast(t({ zh: "两次输入的密码不一致", en: "Passwords do not match" }), "warn");
            return;
        }
        try {
            setIsBusy(true);
            await requestWithParams("PATCH", "/v2/user/password", { old_password: oldPwd, new_password: newPwd });
            setOldPwd("");
            setNewPwd("");
            setNewPwdRepeat("");
            toast(t({ zh: "密码修改成功", en: "Password changed" }), "success");
        } catch (e) {
            toast((e as Error)?.message ?? "Request Failed", "error");
        } finally {
            setIsBusy(false);
        }
    }, [oldPwd, newPwd, newPwdRepeat, toast, t]);

    const saveEmailSettings = useMemo(() => async () => {
         try {
            setIsBusy(true);
            const resp = await requestWithParams<any>("PATCH", "/api/user/email_settings/update", {
                // smtp_server: smtpServer,
                // smtp_port: Number(smtpPort),
                // smtp_username: smtpUser,
                // smtp_password: smtpPass, 
                // sender_email: senderEmail,
                recipient_email: recipientEmail,
                enable_alerts: enableAlerts
            });
            if (resp?.code !== 200) {
                throw new Error(resp?.msg ?? "Request Failed");
            }
            toast(t({ zh: "邮件设置已保存", en: "Email settings saved" }), "success");
        } catch (e) {
            toast((e as Error)?.message ?? "Request Failed", "error");
        } finally {
            setIsBusy(false);
        }
    }, [recipientEmail, enableAlerts, toast, t]);

    const sendTestEmail = useMemo(() => async () => {
         try {
            setIsBusy(true);
            const resp = await requestWithParams<any>("POST", "/api/user/email_settings/test", {});
            if (resp?.code !== 200) {
                throw new Error(resp?.msg ?? "Request Failed");
            }
            toast(t({ zh: "测试邮件已发送", en: "Test email sent" }), "success");
        } catch (e) {
            toast((e as Error)?.message ?? "Request Failed", "error");
        } finally {
            setIsBusy(false);
        }
    }, [toast, t]);

    const applyThemeAction = useMemo(() => async () => {
        setCookie("theme", theme, 180);
        if (theme) {
            document.documentElement.dataset.theme = theme;
        } else {
            delete (document.documentElement.dataset as Record<string, string>)["theme"];
        }
        toast(t({ zh: "主题已应用", en: "Theme applied" }), "success");
    }, [theme, toast, t]);

    const applyTzAction = useMemo(() => async () => {
        setCookie("tz", tz, 180);
        toast(t({ zh: "时区已保存", en: "Timezone saved" }), "success");
    }, [tz, toast, t]);

    const applyLangAction = useMemo(() => async () => {
        setLang(langInput);
        toast(t({ zh: "语言已切换", en: "Language switched", ar: "تم تغيير اللغة" }), "success");
    }, [langInput, toast, t]);

    const applyArabicLayoutDirAction = useMemo(() => async () => {
        setArabicLayoutDir(arabicLayoutDirInput);
        toast(t({ zh: "阿拉伯语布局已更新", en: "Arabic layout updated", ar: "تم تحديث تخطيط العربية" }), "success");
    }, [arabicLayoutDirInput, toast, t]);

    const logoutAction = useMemo(() => async () => {
        try {
            setIsBusy(true);
            await requestDelete("/v2/login");
        } finally {
            expireUserInfo();
            setIsBusy(false);
        }
    }, []);

    if (!isLogined) {
        return <article aria-busy="true" class="container"></article>;
    }

    return (
        <div class="farpov_top_container">
            <PageHeader 
                title={t({ zh: "用户设置", en: "Settings" })} 
                subtitle={t({ zh: "管理您的个人资料、安全设置和界面偏好", en: "Manage your profile, security settings, and UI preferences" })}
                actions={
                    <button class="outline secondary" disabled={isBusy} onClick={logoutAction}>
                        <Icon name="log-out" /> {t({ zh: "注销", en: "Logout" })}
                    </button>
                }
            />
            
            <div style={{ maxWidth: "800px", margin: "0 auto" }}>
                {/* Profile Summary Card */}
                <div class="farpov_card" style={{ marginBottom: "2rem", display: "flex", alignItems: "center", gap: "1.5rem" }}>
                    <div style={{ 
                        width: "80px", height: "80px", 
                        borderRadius: "50%", 
                        background: "var(--pico-primary-background)", 
                        color: "var(--pico-primary-inverse)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "2.5rem", fontWeight: "bold",
                        flexShrink: 0
                    }}>
                        {username ? username.charAt(0).toUpperCase() : "U"}
                    </div>
                    <div style={{ flex: 1 }}>
                        <h3 style={{ marginBottom: "0.25rem", color: "var(--pico-color)" }}>{username}</h3>
                        <p style={{ margin: 0, color: "var(--pico-muted-color)", fontSize: "0.9rem" }}>
                            {organizationName || t({ zh: "未设置组织", en: "No organization set" })}
                        </p>
                    </div>
                </div>

                <Section 
                    title={t({ zh: "基本信息", en: "Basic Information" })} 
                    description={t({ zh: "设置您的显示名称和组织信息", en: "Set your display name and organization details" })}
                >
                    <div class="farpov_card">
                        <div class="farpov_form_group">
                            <label>{t({ zh: "用户名", en: "Username" })}</label>
                            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                <Icon name="user" style={{ color: "var(--pico-muted-color)" }} />
                                <input type="text" value={username} disabled style={{ marginBottom: 0 }} />
                            </div>
                            <div class="farpov_form_hint">
                                {t({ zh: "用户名不可更改", en: "Username cannot be changed" })}
                            </div>
                        </div>
                        
                        <div class="farpov_form_group">
                            <label>{t({ zh: "组织名称", en: "Organization Name" })}</label>
                            <div style={{ display: "flex", gap: "0.5rem" }}>
                                <input
                                    type="text"
                                    value={orgNameInput}
                                    onInput={(e) => setOrgNameInput((e.target as HTMLInputElement).value)}
                                    disabled={isBusy}
                                    placeholder={t({ zh: "输入组织名称", en: "Enter organization name" })}
                                    style={{ marginBottom: 0 }}
                                />
                                <button type="button" aria-busy={isBusy} disabled={isBusy} onClick={saveOrgAction} style={{ width: "auto", marginBottom: 0 }}>
                                    {t({ zh: "保存", en: "Save" })}
                                </button>
                            </div>
                        </div>
                    </div>
                </Section>

                <Section 
                    title={t({ zh: "安全设置", en: "Security" })} 
                    description={t({ zh: "修改您的登录密码", en: "Change your login password" })}
                >
                    <div class="farpov_card">
                        <div class="farpov_form_group">
                            <label>{t({ zh: "旧密码", en: "Old Password" })}</label>
                            <input type="password" value={oldPwd} onInput={(e) => setOldPwd((e.target as HTMLInputElement).value)} disabled={isBusy} />
                        </div>
                        
                        <div class="grid">
                            <div class="farpov_form_group">
                                <label>{t({ zh: "新密码", en: "New Password" })}</label>
                                <input type="password" value={newPwd} onInput={(e) => setNewPwd((e.target as HTMLInputElement).value)} disabled={isBusy} />
                            </div>
                            <div class="farpov_form_group">
                                <label>{t({ zh: "重复新密码", en: "Repeat New Password" })}</label>
                                <input
                                    type="password"
                                    value={newPwdRepeat}
                                    onInput={(e) => setNewPwdRepeat((e.target as HTMLInputElement).value)}
                                    disabled={isBusy}
                                />
                            </div>
                        </div>
                        
                        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1rem" }}>
                            <button type="button" class="outline secondary" aria-busy={isBusy} disabled={isBusy} onClick={changePasswordAction} style={{ width: "auto", marginBottom: 0 }}>
                                <Icon name="lock" size={16} style={{ marginRight: "0.5rem" }} />
                                {t({ zh: "修改密码", en: "Update Password" })}
                            </button>
                        </div>
                    </div>
                </Section>

                <Section
                    title={t({ zh: "子账户管理", en: "Sub-Accounts" })}
                    description={t({ zh: "创建和管理拥有查看权限的子账户", en: "Manage sub-accounts with read-only access" })}
                >
                    <div class="farpov_card">
                        {/* Permission Description Box */}
                        <div style={{
                            background: "var(--pico-card-background-color)",
                            border: "1px solid var(--pico-muted-border-color)",
                            borderRadius: "var(--pico-border-radius)",
                            padding: "1rem",
                            marginBottom: "1.5rem",
                            fontSize: "0.9rem"
                        }}>
                            <h6 style={{ marginBottom: "0.5rem", display: "flex", alignItems: "center", gap: "0.5rem", color: "var(--pico-primary)" }}>
                                <Icon name="info" size={16} />
                                {t({ zh: "子账户权限说明", en: "Permission Guide" })}
                            </h6>
                            <ul style={{ margin: 0, paddingLeft: "1.2rem", color: "var(--pico-muted-color)" }}>
                                <li>
                                    <strong>{t({ zh: "允许操作：", en: "Allowed: " })}</strong>
                                    {t({ 
                                        zh: "查看分配相机的实时监控、浏览/下载照片、生成延时视频。", 
                                        en: "View live feed, browse/download photos, create time-lapse videos." 
                                    })}
                                </li>
                                <li style={{ marginTop: "0.25rem" }}>
                                    <strong>{t({ zh: "禁止操作：", en: "Restricted: " })}</strong>
                                    {t({ 
                                        zh: "删除照片、修改相机配置、管理存储设置、修改账户信息。", 
                                        en: "Delete photos, change camera settings, manage storage, modify account info." 
                                    })}
                                </li>
                            </ul>
                        </div>

                        <div style={{ marginBottom: "1rem" }}>
                            <button type="button" onClick={() => setShowSubModal(true)} disabled={isBusy} style={{ width: "auto", marginBottom: 0 }}>
                                <Icon name="plus" size={16} style={{ marginRight: "0.5rem" }} />
                                {t({ zh: "创建子账户", en: "Create Sub-Account" })}
                            </button>
                        </div>
                        
                        {subAccounts.length > 0 ? (
                            <>
                                <div class="farpov_sub_accounts_table farpov_data_grid">
                                    <table role="grid">
                                        <thead>
                                            <tr>
                                                <th>{t({ zh: "用户名", en: "Username" })}</th>
                                                <th>{t({ zh: "备注", en: "Remark" })}</th>
                                                <th>{t({ zh: "操作", en: "Actions" })}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {subAccounts.map(sub => (
                                                <tr key={sub.id}>
                                                    <td>{sub.username}</td>
                                                    <td>{sub.remark}</td>
                                                    <td>
                                                        <div style={{ display: "flex", gap: "0.5rem" }}>
                                                            <button
                                                                class="outline secondary"
                                                                style={{ padding: "0.2rem 0.5rem", fontSize: "0.8rem", width: "auto" }}
                                                                onClick={() => openPermModal(sub)}
                                                            >
                                                                {t({ zh: "分配权限", en: "Permissions" })}
                                                            </button>
                                                            <button
                                                                class="outline danger"
                                                                style={{ padding: "0.2rem 0.5rem", fontSize: "0.8rem", width: "auto", color: "var(--pico-del-color)", borderColor: "var(--pico-del-color)" }}
                                                                onClick={() => deleteSubAccountAction(sub.id)}
                                                            >
                                                                {t({ zh: "删除", en: "Delete" })}
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div class="farpov_sub_accounts_cards">
                                    {subAccounts.map((sub) => (
                                        <div class="farpov_sub_account_card" key={sub.id}>
                                            <div class="farpov_sub_account_card_header">
                                                <div class="farpov_sub_account_card_title">{sub.username}</div>
                                            </div>
                                            <div class="farpov_sub_account_card_body">
                                                <div class="farpov_sub_account_kv">
                                                    <div class="k">{t({ zh: "备注", en: "Remark" })}</div>
                                                    <div class="v">{sub.remark || "-"}</div>
                                                </div>
                                            </div>
                                            <div class="farpov_sub_account_card_actions">
                                                <button
                                                    class="outline secondary"
                                                    style={{ width: "auto", marginBottom: 0 }}
                                                    onClick={() => openPermModal(sub)}
                                                >
                                                    {t({ zh: "分配权限", en: "Permissions" })}
                                                </button>
                                                <button
                                                    class="outline danger"
                                                    style={{ width: "auto", marginBottom: 0, color: "var(--pico-del-color)", borderColor: "var(--pico-del-color)" }}
                                                    onClick={() => deleteSubAccountAction(sub.id)}
                                                >
                                                    {t({ zh: "删除", en: "Delete" })}
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <p style={{ color: "var(--pico-muted-color)", textAlign: "center", margin: "2rem 0" }}>
                                {t({ zh: "暂无子账户", en: "No sub-accounts created" })}
                            </p>
                        )}
                    </div>
                </Section>

                <Section 
                    title={t({ zh: "邮件报警设置", en: "Email Alert Settings" })} 
                    description={t({ zh: "配置 SMTP 发送报警邮件", en: "Configure SMTP to send alert emails" })}
                >
                    <div class="farpov_card">
                        <div class="farpov_form_group">
                            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                <input type="checkbox" checked={enableAlerts} onChange={(e) => setEnableAlerts((e.target as HTMLInputElement).checked)} disabled={isBusy} />
                                {t({ zh: "启用报警邮件", en: "Enable Alert Emails" })}
                            </label>
                        </div>

                        {enableAlerts && (
                            <>
                                {/* Deprecated SMTP fields
                                <div class="grid">
                                    <div class="farpov_form_group">
                                        <label>SMTP Server</label>
                                        <input type="text" value={smtpServer} onInput={(e) => setSmtpServer((e.target as HTMLInputElement).value)} disabled={isBusy} placeholder="smtp.gmail.com" />
                                    </div>
                                    <div class="farpov_form_group">
                                        <label>SMTP Port</label>
                                        <input type="number" value={smtpPort} onInput={(e) => setSmtpPort(Number((e.target as HTMLInputElement).value))} disabled={isBusy} placeholder="587" />
                                    </div>
                                </div>
                                <div class="grid">
                                    <div class="farpov_form_group">
                                        <label>SMTP Username</label>
                                        <input type="text" value={smtpUser} onInput={(e) => setSmtpUser((e.target as HTMLInputElement).value)} disabled={isBusy} />
                                    </div>
                                    <div class="farpov_form_group">
                                        <label>SMTP Password</label>
                                        <input type="password" value={smtpPass} onInput={(e) => setSmtpPass((e.target as HTMLInputElement).value)} disabled={isBusy} placeholder="******" />
                                    </div>
                                </div>
                                <div class="grid">
                                    <div class="farpov_form_group">
                                        <label>{t({ zh: "发件人邮箱 (可选)", en: "Sender Email (Optional)" })}</label>
                                        <input type="email" value={senderEmail} onInput={(e) => setSenderEmail((e.target as HTMLInputElement).value)} disabled={isBusy} placeholder="alert@example.com" />
                                    </div>
                                */}
                                    <div class="farpov_form_group">
                                        <label>{t({ zh: "收件人邮箱", en: "Recipient Email" })}</label>
                                        <input type="email" value={recipientEmail} onInput={(e) => setRecipientEmail((e.target as HTMLInputElement).value)} disabled={isBusy} placeholder="me@example.com" />
                                    </div>
                                {/* </div> */}
                                
                                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1rem", gap: "1rem" }}>
                                    <button type="button" class="outline secondary" aria-busy={isBusy} disabled={isBusy} onClick={sendTestEmail} style={{ width: "auto", marginBottom: 0 }}>
                                        <Icon name="mail" size={16} style={{ marginRight: "0.5rem" }} />
                                        {t({ zh: "发送测试邮件", en: "Send Test Email" })}
                                    </button>
                                    <button type="button" aria-busy={isBusy} disabled={isBusy} onClick={saveEmailSettings} style={{ width: "auto", marginBottom: 0 }}>
                                        <Icon name="save" size={16} style={{ marginRight: "0.5rem" }} />
                                        {t({ zh: "保存设置", en: "Save Settings" })}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </Section>

                <Section 
                    title={t({ zh: "界面偏好", en: "Interface Preferences" })} 
                    description={t({ zh: "自定义系统外观和时区", en: "Customize system appearance and timezone" })}
                >
                    <div class="farpov_card">
                        <div class="grid">
                            <div class="farpov_form_group">
                                <label>{t({ zh: "语言", en: "Language" })}</label>
                                <div style={{ display: "flex", gap: "0.5rem" }}>
                                    <select value={langInput} onChange={(e) => setLangInput((e.target as HTMLSelectElement).value as LanguageCode)} disabled={isBusy} style={{ marginBottom: 0 }}>
                                        <option value="zh">{t({ zh: "中文", en: "Chinese", ar: "الصينية" })}</option>
                                        <option value="en">{t({ zh: "英语", en: "English", ar: "الإنجليزية" })}</option>
                                        <option value="ar">{t({ zh: "阿拉伯语", en: "Arabic", ar: "العربية" })}</option>
                                    </select>
                                    <button type="button" class="secondary outline" disabled={isBusy} onClick={applyLangAction} style={{ width: "auto", marginBottom: 0, padding: "0.4rem 1rem" }}>
                                        <Icon name="check" />
                                    </button>
                                </div>
                            </div>
                            
                            {langInput === "ar" && (
                                <div class="farpov_form_group">
                                    <label>{t({ zh: "阿拉伯语布局", en: "Arabic Layout", ar: "اتجاه العربية" })}</label>
                                    <div style={{ display: "flex", gap: "0.5rem" }}>
                                        <select
                                            value={arabicLayoutDirInput}
                                            onChange={(e) => setArabicLayoutDirInput((e.target as HTMLSelectElement).value as ArabicLayoutDir)}
                                            disabled={isBusy}
                                            style={{ marginBottom: 0 }}
                                        >
                                            <option value="rtl">{t({ zh: "RTL 布局", en: "RTL layout", ar: "من اليمين إلى اليسار" })}</option>
                                            <option value="ltr">{t({ zh: "保持 LTR", en: "Keep LTR", ar: "من اليسار إلى اليمين" })}</option>
                                        </select>
                                        <button type="button" class="secondary outline" disabled={isBusy} onClick={applyArabicLayoutDirAction} style={{ width: "auto", marginBottom: 0, padding: "0.4rem 1rem" }}>
                                            <Icon name="check" />
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div class="farpov_form_group">
                                <label>{t({ zh: "主题", en: "Theme" })}</label>
                                <div style={{ display: "flex", gap: "0.5rem" }}>
                                    <select value={theme} onChange={(e) => setTheme((e.target as HTMLSelectElement).value)} disabled={isBusy} style={{ marginBottom: 0 }}>
                                        <option value="">{t({ zh: "跟随默认", en: "Default" })}</option>
                                        <option value="light">{t({ zh: "浅色", en: "Light" })}</option>
                                        <option value="dark">{t({ zh: "深色", en: "Dark" })}</option>
                                    </select>
                                    <button type="button" class="secondary outline" disabled={isBusy} onClick={applyThemeAction} style={{ width: "auto", marginBottom: 0, padding: "0.4rem 1rem" }}>
                                        <Icon name="check" />
                                    </button>
                                </div>
                            </div>
                            
                            <div class="farpov_form_group">
                                <label>{t({ zh: "时区", en: "Timezone" })}</label>
                                <div style={{ display: "flex", gap: "0.5rem" }}>
                                    <select value={tz} onChange={(e) => setTz((e.target as HTMLSelectElement).value)} disabled={isBusy} style={{ marginBottom: 0 }}>
                                        <option value="">{t({ zh: "跟随浏览器", en: "Browser default" })}</option>
                                        {TZ_LIST.map((z) => (
                                            <option key={z} value={z}>
                                                {z}
                                            </option>
                                        ))}
                                    </select>
                                    <button type="button" class="secondary outline" disabled={isBusy} onClick={applyTzAction} style={{ width: "auto", marginBottom: 0, padding: "0.4rem 1rem" }}>
                                        <Icon name="check" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </Section>

                <Section 
                    title={t({ zh: "服务支持", en: "Service & Support" })} 
                    description={t({ zh: "会员续费与设备维护", en: "Membership renewal and maintenance" })}
                >
                    <div class="farpov_card" id="service-support-section">
                        {contactInfo && (contactInfo.contact_name || contactInfo.email || contactInfo.whatsapp) ? (
                            <>
                                <div style={{ display: "flex", alignItems: "center", gap: "1rem", padding: "1rem", background: "var(--pico-card-sectioning-background-color)", borderRadius: "var(--pico-border-radius)" }}>
                                    <div style={{ padding: "0.8rem", background: "var(--pico-primary-background)", color: "var(--pico-primary-inverse)", borderRadius: "50%" }}>
                                        <Icon name="life-buoy" size={24} />
                                    </div>
                                    <div>
                                        <h5 style={{ margin: "0 0 0.5rem 0" }}>
                                            {contactInfo.contact_name || t({ zh: "联系厂家", en: "Contact Manufacturer" })}
                                        </h5>
                                        <p style={{ margin: 0, fontSize: "0.9rem", color: "var(--pico-muted-color)" }}>
                                            {t({ zh: "如需会员续费或设备维护，请通过以下方式联系我们。", en: "Please contact us for renewal or maintenance." })}
                                        </p>
                                    </div>
                                </div>
                                
                                <div class="grid" style={{ marginTop: "1.5rem" }}>
                                    {contactInfo.website && (
                                        <div class="farpov_form_group">
                                            <label>{t({ zh: "官方网站", en: "Website" })}</label>
                                            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                                <Icon name="globe" style={{ color: "var(--pico-muted-color)" }} />
                                                <a href={contactInfo.website} target="_blank" rel="noopener noreferrer">{contactInfo.website}</a>
                                            </div>
                                        </div>
                                    )}
                                    {contactInfo.email && (
                                        <div class="farpov_form_group">
                                            <label>{t({ zh: "客服邮箱", en: "Support Email" })}</label>
                                            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                                <Icon name="mail" style={{ color: "var(--pico-muted-color)" }} />
                                                <span>{contactInfo.email}</span>
                                            </div>
                                        </div>
                                    )}
                                    {contactInfo.whatsapp && (
                                        <div class="farpov_form_group">
                                            <label>WhatsApp</label>
                                            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                                <Icon name="message-circle" style={{ color: "var(--pico-muted-color)" }} />
                                                <span>{contactInfo.whatsapp}</span>
                                            </div>
                                        </div>
                                    )}
                                    {contactInfo.wechat_id && (
                                        <div class="farpov_form_group">
                                            <label>{t({ zh: "微信", en: "WeChat" })}</label>
                                            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                                <Icon name="message-circle" style={{ color: "var(--pico-muted-color)" }} />
                                                <span>{contactInfo.wechat_id}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : (
                            <div style={{ padding: "2rem", textAlign: "center", color: "var(--pico-muted-color)" }}>
                                <Icon name="life-buoy" size={32} style={{ marginBottom: "1rem", opacity: 0.5 }} />
                                <p>{t({ zh: "暂无联系信息", en: "No contact info available" })}</p>
                            </div>
                        )}
                    </div>
                </Section>
            </div>

            <Modal
                title={t({ zh: "创建子账户", en: "Create Sub-Account" })}
                visible={showSubModal}
                onClose={() => setShowSubModal(false)}
            >
                {subAccountLimitReached ? (
                    <article style={{ background: "rgba(255, 193, 7, 0.12)", padding: "0.75rem" }}>
                        <strong>{t({ zh: "子账户数量已达上限", en: "Sub-account limit reached" })}</strong>
                        <div style={{ marginTop: "0.25rem", opacity: 0.85 }}>
                            {t({ zh: "每个主账户最多创建 5 个子账户。", en: "Each main account can create up to 5 sub-accounts." })}
                        </div>
                    </article>
                ) : null}
                <div class="farpov_form_group">
                    <label>{t({ zh: "用户名", en: "Username" })}</label>
                    <input type="text" value={subUsername} onInput={(e) => setSubUsername((e.target as HTMLInputElement).value)} />
                    <div class="farpov_form_hint">
                        {t({ zh: "长度 8-64，仅允许字母数字及 ._-，且首尾为字母数字", en: "8-64 chars, A-Z a-z 0-9 . _ -, start/end alnum" })}
                    </div>
                </div>
                <div class="farpov_form_group">
                    <label>{t({ zh: "密码", en: "Password" })}</label>
                    <input type="password" value={subPassword} onInput={(e) => setSubPassword((e.target as HTMLInputElement).value)} />
                    <div class="farpov_form_hint">
                        {t({ zh: "长度 8-64，需包含字母和数字，首尾不能有空格", en: "8-64 chars, must include letters and numbers, no leading/trailing spaces" })}
                    </div>
                </div>
                <div class="farpov_form_group">
                    <label>{t({ zh: "备注", en: "Remark" })}</label>
                    <input type="text" value={subRemark} onInput={(e) => setSubRemark((e.target as HTMLInputElement).value)} />
                    <div class="farpov_form_hint">{t({ zh: "可选，最多 50 个字符", en: "Optional, max 50 characters" })}</div>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "1rem", marginTop: "1rem" }}>
                    <button class="outline secondary" onClick={() => setShowSubModal(false)}>{t({ zh: "取消", en: "Cancel" })}</button>
                    <button disabled={isBusy || subAccountLimitReached} onClick={createSubAccountAction}>{t({ zh: "创建", en: "Create" })}</button>
                </div>
            </Modal>

            <Modal
                title={t({ zh: "分配相机权限", en: "Assign Camera Permissions" })}
                visible={showPermModal}
                onClose={() => setShowPermModal(false)}
            >
                <p style={{ fontSize: "0.9rem", color: "var(--pico-muted-color)" }}>
                    {t({ zh: "选择该子账户可以查看的相机", en: "Select cameras this sub-account can view" })}
                </p>
                <div style={{ maxHeight: "300px", overflowY: "auto", border: "1px solid var(--pico-muted-border-color)", borderRadius: "4px", padding: "0.5rem" }}>
                    {allCameras.map(cam => (
                        <label key={cam.camera_id} style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.25rem 0" }}>
                            <input 
                                type="checkbox" 
                                checked={selectedCameras.includes(cam.camera_id)}
                                onChange={(e) => {
                                    if ((e.target as HTMLInputElement).checked) {
                                        setSelectedCameras([...selectedCameras, cam.camera_id]);
                                    } else {
                                        setSelectedCameras(selectedCameras.filter(id => id !== cam.camera_id));
                                    }
                                }}
                            />
                            <span>{cam.camera_name}</span>
                        </label>
                    ))}
                    {allCameras.length === 0 && <p>{t({ zh: "无可用相机", en: "No cameras available" })}</p>}
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "1rem", marginTop: "1rem" }}>
                    <button class="outline secondary" onClick={() => setShowPermModal(false)}>{t({ zh: "取消", en: "Cancel" })}</button>
                    <button onClick={savePermAction}>{t({ zh: "保存", en: "Save" })}</button>
                </div>
            </Modal>
        </div>
    );
};

export default UserSettingsPage;
